import { loadDanbooruTags, searchTags, isForbiddenTag, getTagByNameOrAlias, isRatingPromptTag, normalizeTagForLookup } from './danbooruTags.js';
import { findActiveTagStart, readActiveTagWord } from './promptSyntax.js';
import { initializeRichPromptEditor } from './richPromptEditor.js';

const OUTFIT_FORBIDDEN_CATEGORIES = [3, 4, 7];
const CHARACTER_FORBIDDEN_CATEGORIES = [6, 7];
const RICH_EDITOR_SUGGESTION_TYPES = new Set(['outfitPrompt', 'characterPrompt', 'spriteActionPrompt', 'bannerPrompt', 'negativePrompt']);

export const OUTFIT_PROMPT_AUTOCOMPLETE_OPTIONS = { forbiddenCategories: OUTFIT_FORBIDDEN_CATEGORIES };

export const NEGATIVE_PROMPT_AUTOCOMPLETE_OPTIONS = {
  forbiddenCategories: [],
  replaceForbiddenCategories: true,
};

export function initializeOutfitPromptAutocomplete(textareaId) {
  return initializePromptAutocomplete(textareaId, 'outfitPrompt', null, OUTFIT_PROMPT_AUTOCOMPLETE_OPTIONS);
}

export function initializeNegativePromptAutocomplete(textareaId) {
  return initializePromptAutocomplete(textareaId, 'negativePrompt', null, NEGATIVE_PROMPT_AUTOCOMPLETE_OPTIONS);
}

export async function initializePromptAutocomplete(textareaId, suggestionType, scenarioTagsList = null, options = {}) {
  const textarea = document.getElementById(textareaId);
  if (!textarea) return;

  const useDanbooruTags = ['characterPrompt', 'bannerPrompt', 'outfitPrompt', 'backgroundPrompt', 'spriteActionPrompt', 'negativePrompt'].includes(suggestionType);
  const useScenarioTags = suggestionType === 'scenarioTags';
  const forbiddenCategories = Array.isArray(options.forbiddenCategories)
    ? options.forbiddenCategories
    : suggestionType === 'outfitPrompt'
      ? OUTFIT_FORBIDDEN_CATEGORIES
      : suggestionType === 'characterPrompt'
        ? CHARACTER_FORBIDDEN_CATEGORIES
        : undefined;
  const tagFilterOptions = {
    suggestionType,
    forbiddenCategories,
    replaceForbiddenCategories: options.replaceForbiddenCategories === true,
    forbidAppearanceCountTags: options.forbidAppearanceCountTags === true,
  };

  if (useDanbooruTags) await loadDanbooruTags();

  const richEditor = RICH_EDITOR_SUGGESTION_TYPES.has(suggestionType)
    ? initializeRichPromptEditor(textarea, {
      isForbiddenTag: (tagName) => {
        const lookupTagName = normalizeTagForLookup(tagName);
        if (!lookupTagName) {
          return false;
        }

        return isForbiddenTag(lookupTagName, tagFilterOptions);
      },
      isUnrecognizedTag: (tagName) => {
        const trimmed = String(tagName || '').trim();
        if (/[\[\]]/.test(trimmed)) return true;
        const braceMatch = trimmed.match(/^\{([^{}]+)\}$/);
        if (braceMatch && typeof options.isLinkedBraceToken === 'function') {
          return !options.isLinkedBraceToken(String(braceMatch[1] || '').trim());
        }
        const lookupTagName = normalizeTagForLookup(tagName);
        if (isRatingPromptTag(lookupTagName)) return false;
        return Boolean(lookupTagName) && !getTagByNameOrAlias(lookupTagName);
      }
    })
    : null;
  const inputAdapter = createInputAdapter(textarea, richEditor);

  if (richEditor) {
    const currentValue = textarea.value || '';
    const currentSelectionStart = typeof textarea.selectionStart === 'number'
      ? textarea.selectionStart
      : currentValue.length;
    richEditor.setValue(currentValue, currentSelectionStart, false);
  }

  const suggestions = [];
  const container = createSuggestionsContainer(textarea, suggestionType);
  let selectedIndex = -1;
  let currentSuggestions = [];
  let lastKnownSelectionStart = 0;

  const cacheSelectionStart = () => {
    lastKnownSelectionStart = inputAdapter.getSelectionStart();
  };

  const updatePosition = () => positionSuggestions(inputAdapter.inputElement, container, suggestionType);
  const handleInput = async () => {
    cacheSelectionStart();
    const word = readActiveTagWord(
      inputAdapter.getValue().slice(0, inputAdapter.getSelectionStart()),
      !useDanbooruTags
    );
    if (!word) return hide(container);

    if (useDanbooruTags) {
      currentSuggestions = searchTags(word, 15, tagFilterOptions);
    } else if (useScenarioTags && scenarioTagsList) {
      currentSuggestions = getScenarioTagSuggestions(word, scenarioTagsList);
    } else {
      currentSuggestions = getPromptSuggestions(inputAdapter.getValue(), suggestions);
    }

    if (!currentSuggestions.length) return hide(container);

    renderSuggestions(container, currentSuggestions, selectedIndex, useDanbooruTags, useScenarioTags);
    show(container);
    updatePosition();
    selectedIndex = -1;
  };

  inputAdapter.inputElement.addEventListener('input', handleInput);
  inputAdapter.inputElement.addEventListener('scroll', updatePosition);
  inputAdapter.inputElement.addEventListener('click', () => {
    cacheSelectionStart();
    updatePosition();
  });
  inputAdapter.inputElement.addEventListener('keyup', (e) => ['ArrowLeft', 'ArrowRight'].includes(e.key) && updatePosition());
  inputAdapter.inputElement.addEventListener('keydown', cacheSelectionStart);

  inputAdapter.inputElement.addEventListener('keydown', async (e) => {
    if (!currentSuggestions.length) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        selectedIndex = Math.min(selectedIndex + 1, currentSuggestions.length - 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        selectedIndex = Math.max(selectedIndex - 1, 0);
        break;
      case 'Tab':
      case 'Enter':
        if (selectedIndex >= 0) {
          e.preventDefault();
          await selectSuggestion(inputAdapter, currentSuggestions[selectedIndex], useDanbooruTags, useScenarioTags, suggestions, suggestionType, inputAdapter.getSelectionStart());
          hide(container);
          currentSuggestions = [];
          selectedIndex = -1;
        }
        break;
      case 'Escape':
        hide(container);
        currentSuggestions = [];
        selectedIndex = -1;
        break;
    }
    updateSelection(container, selectedIndex);
  });

  container.addEventListener('mousedown', (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const item = target.closest('.suggestion-item');
    if (!item) return;
    cacheSelectionStart();
    e.preventDefault();
  });

  container.addEventListener('click', async (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const item = target.closest('.suggestion-item');
    if (!item) return;

    const index = parseInt(item.dataset.index);
    await selectSuggestion(inputAdapter, currentSuggestions[index], useDanbooruTags, useScenarioTags, suggestions, suggestionType, lastKnownSelectionStart);
    hide(container);
    currentSuggestions = [];
    selectedIndex = -1;
    inputAdapter.focus();
  });

  document.addEventListener('click', (e) => {
    const targetNode = e.target;
    if (!(targetNode instanceof Node)) return;
    if (!container.contains(targetNode) && !inputAdapter.containsTarget(targetNode)) {
      hide(container);
      currentSuggestions = [];
      selectedIndex = -1;
    }
  });
}

function createSuggestionsContainer(textarea, type) {
  const container = document.createElement('div');
  container.className = 'prompt-suggestions is-hidden';

  const containerId = { bannerPrompt: 'banner-suggestions-container', characterPrompt: 'character-suggestions-container', outfitPrompt: 'outfit-suggestions-container', backgroundPrompt: 'background-suggestions-container', scenarioTags: 'scenario-suggestions-container' }[type];
  const parent = document.getElementById(containerId) || textarea.parentElement;

  if (containerId && !document.getElementById(containerId)) {
    textarea.parentElement.classList.add('prompt-suggestions-host');
    textarea.parentElement.appendChild(container);
  } else {
    parent.classList.add('prompt-suggestions-host');
    parent.appendChild(container);
  }

  return container;
}

function createInputAdapter(textarea, richEditor) {
  if (!richEditor) {
    return {
      inputElement: textarea,
      getValue: () => textarea.value,
      getSelectionStart: () => textarea.selectionStart || 0,
      setValue: (nextValue, selectionStart) => {
        textarea.value = nextValue;
        textarea.setSelectionRange(selectionStart, selectionStart);
        textarea.dispatchEvent(new Event('input', { bubbles: true }));
      },
      focus: () => textarea.focus(),
      containsTarget: (target) => target === textarea
    };
  }

  return {
    inputElement: richEditor.editor,
    getValue: () => richEditor.getValue(),
    getSelectionStart: () => richEditor.getSelectionStart(),
    setValue: (nextValue, selectionStart) => richEditor.setValue(nextValue, selectionStart, true),
    focus: () => richEditor.focus(),
    containsTarget: (target) => richEditor.containsTarget(target)
  };
}

function getTagBoundaryIndex(textBeforePosition, splitOnSpaces = true) {
  return findActiveTagStart(textBeforePosition, splitOnSpaces) - 1;
}

function positionSuggestions(inputElement, container, type) {
  const containerId = { bannerPrompt: 'banner-suggestions-container', characterPrompt: 'character-suggestions-container', outfitPrompt: 'outfit-suggestions-container', backgroundPrompt: 'background-suggestions-container', scenarioTags: 'scenario-suggestions-container' }[type];
  const parent = document.getElementById(containerId);
  const gapPx = 2;

  if (parent) {
    // Normal dropdown behavior: anchor under the textarea, not at the caret.
    container.style.left = '0px';
    container.style.top = '0px';
  } else {
    // Fallback: position directly under the textarea within its parent container.
    container.style.left = '0px';
    container.style.top = `${inputElement.offsetTop + inputElement.offsetHeight + gapPx}px`;
  }
}

function renderSuggestions(container, suggestions, selected, useDanbooru, useScenarioTags) {
  container.innerHTML = suggestions.map((s, i) => {
    const selectedClass = i === selected ? ' selected' : '';
    if (useDanbooru) {
      const forbiddenClass = s.isForbidden ? ' forbidden' : '';
      return `<div class="suggestion-item${selectedClass}${forbiddenClass}" data-index="${i}"><span class="suggestion-text">${s.displayText}</span><span class="suggestion-description">Booru count: ${s.n}</span></div>`;
    } else if (useScenarioTags) {
      return `<div class="suggestion-item${selectedClass}" data-index="${i}"><span class="suggestion-text">${s}</span></div>`;
    }
    return `<div class="suggestion-item${selectedClass}" data-index="${i}"><span class="suggestion-text">${s.text}</span><span class="suggestion-description">${s.description}</span><span class="suggestion-usage">(Used: ${s.usage || 0})</span></div>`;
  }).join('');
}

function updateSelection(container, index) {
  container.querySelectorAll('.suggestion-item').forEach((item, i) => {
    item.classList.toggle('selected', i === index);
  });
}

function hide(container) {
  container.classList.add('is-hidden');
}

function show(container) {
  container.classList.remove('is-hidden');
}

function getPromptSuggestions(input, suggestions) {
  if (!input) return [...suggestions];
  const last = input.split(',').pop().trim().toLowerCase();
  return suggestions.filter(s => s.text.toLowerCase().startsWith(last));
}

function getScenarioTagSuggestions(word, scenarioTagsList) {
  if (!word) return scenarioTagsList.slice(0, 15);
  const wordLower = word.toLowerCase();
  return scenarioTagsList
    .filter(tag => tag.toLowerCase().includes(wordLower))
    .slice(0, 15);
}

function transformTagText(tag) {
  return tag.replace(/_/g, ' ').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
}

async function selectSuggestion(inputAdapter, suggestion, useDanbooru, useScenarioTags, suggestions, type, insertionPosition = null) {
  let tag;
  if (useDanbooru) {
    tag = suggestion.t;
  } else if (useScenarioTags) {
    tag = suggestion; // For scenario tags, suggestion is just the string
  } else {
    tag = suggestion.text;
  }

  const transformed = transformTagText(tag);
  const pos = typeof insertionPosition === 'number' ? insertionPosition : inputAdapter.getSelectionStart();
  const text = inputAdapter.getValue();
  const textBefore = text.substring(0, pos);
  const last = getTagBoundaryIndex(textBefore, !useDanbooru);
  const before = text.substring(0, last + 1);
  const after = text.substring(pos);

  const newValue = `${before}${transformed}, ${after}`;
  const nextPosition = before.length + transformed.length + 2;
  inputAdapter.setValue(newValue, nextPosition);

  if (!useDanbooru && !useScenarioTags) {
    const index = suggestions.findIndex(s => s.text === tag);
    if (index !== -1) {
      suggestions[index].usage = (suggestions[index].usage || 0) + 1;
      suggestions.sort((a, b) => (b.usage || 0) - (a.usage || 0));
    }
  }
}
